import {
  ApiError,
  Badge,
  Bar,
  BrandMark,
  Button,
  EmptyState,
  ErrorState,
  Modal,
  budgetLevel,
} from "@corecrm/ui";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { Link, type NavigateFunction } from "react-router-dom";
import { useCompany, useMe, useProjects } from "../api/queries";
import type { Company } from "../api/types";
import { BILLING_ONLY_NOTE, statusLabel } from "../lib/labels";

/** A status in the client's words ("submitted" reads "Awaiting your approval"). */
/** Whether the signed-in client is one of the account's billing contacts (UX-192). */
export function useIsBilling(): boolean {
  return useMe().data?.billing_contact ?? false;
}

/** A page only billing contacts see (invoices): anyone else gets a note instead (UX-192). The
    API refuses them too. */
export function BillingOnly({ children }: { children: ReactNode }) {
  if (useIsBilling()) return <>{children}</>;
  return (
    <EmptyState>
      <h2>For billing contacts</h2>
      <p>{BILLING_ONLY_NOTE}</p>
    </EmptyState>
  );
}

export function Status({ status }: { status: string }) {
  const [label, kind] = statusLabel(status);
  return <Badge kind={kind}>{label}</Badge>;
}

/** A failed load. Another account's record is a 404, shown the same as one that doesn't exist. */
export function LoadError({
  error,
  onRetry,
  what,
  back,
}: {
  error: unknown;
  onRetry?: () => void;
  what: string;
  back: [string, string];
}) {
  if (error instanceof ApiError && error.status === 404) return <NotAvailable what={what} back={back} />;
  return <ErrorState error={error} onRetry={onRetry} />;
}

/** "This bug is not available": the same words for a record that doesn't exist, one that is
    hidden, and one on a project you can't see, so nothing says which it is. */
export function NotAvailable({ what, back }: { what: string; back: [string, string] }) {
  return (
    <EmptyState
      action={
        <Link className="btn" to={back[0]}>
          {back[1]}
        </Link>
      }
    >
      <p>{`This ${what} is not available.`}</p>
      <p className="muted">
        The link may be out of date, or it may belong to another account. If you think you should see it,
        reply to the email you received from CoreNode.
      </p>
    </EmptyState>
  );
}

/** Close a bug or request dialog: opened from the list inside the app, go back (so Back still
    leads where you came from); opened from a link, replace the address with the list's. */
export function closeRecord(navigate: NavigateFunction, state: unknown, list: string) {
  if ((state as { fromList?: boolean } | null)?.fromList) navigate(-1);
  else navigate(list, { replace: true });
}

/** A record opened from a link (a bug, a feature request) that is missing, hidden, or on a project
    you can't see: the same dialog for all three. Any other failure gets the usual error. */
export function RecordUnavailable({
  what,
  error,
  onClose,
}: {
  what: string;
  error: unknown;
  onClose: () => void;
}) {
  const missing = !error || (error instanceof ApiError && error.status === 404);
  return (
    <Modal
      title={missing ? `This ${what} is not available` : `Could not load the ${what}`}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {missing ? (
        <p className="muted">
          The link may be out of date, or it may belong to another account. If you think you should see it,
          reply to the email you received from CoreNode.
        </p>
      ) : (
        <ErrorState error={error} />
      )}
    </Modal>
  );
}

/** Hours used against a budget: a coloured bar and "12 h of 40 h". */
export function HoursBar({ used, budget, label }: { used: number; budget: number | null; label: string }) {
  const pct = budget ? (used / budget) * 100 : 0;
  return (
    <div className="minibar">
      <Bar pct={pct} level={budgetLevel(pct)} label={label} />
      <span className="mono">
        {fmtH(used)}
        {budget ? ` of ${fmtH(budget)}` : ""}
      </span>
    </div>
  );
}

const fmtH = (n: number) => `${Math.round(n * 100) / 100} h`;

export function Crumb({ to, label, children }: { to: string; label: string; children: ReactNode }) {
  return (
    <div className="crumb">
      <Link to={to}>{label}</Link> / {children}
    </div>
  );
}

/** A link to one of the client's projects, by name once the project list has loaded. */
export function ProjectLink({ id }: { id: string }) {
  const name = useProjects().data?.find((p) => p.id === id)?.name;
  return <Link to={`/projects/${id}`}>{name ?? "your project"}</Link>;
}

/** "CoreNode Software Limited · Halifax, Nova Scotia", from the company settings. */
export function companyLine(company: Company | undefined): string {
  return [company?.name, company?.address].filter(Boolean).join(" · ");
}

/** The card the signed-out pages sit in (sign in, joining, password reset). */
export function PublicShell({ title, children }: { title: string; children: ReactNode }) {
  const company = useCompany().data;
  useEffect(() => {
    document.body.classList.add("joining");
    document.title = `${title} · CoreNode client portal`;
    return () => document.body.classList.remove("joining");
  }, [title]);
  return (
    <main className="join-page">
      <div className="join">
        <div className="join-card">
          <div className="join-brand">
            <BrandMark />
            <div>
              <strong>CoreNode client portal</strong>
              <span className="faint">{companyLine(company)}</span>
            </div>
          </div>
          {children}
        </div>
        <p className="faint" style={{ textAlign: "center" }}>
          Questions? Reply to the email you received from CoreNode
          {company?.email ? (
            <>
              , or write to <a href={`mailto:${company.email}`}>{company.email}</a>
            </>
          ) : null}
          .
        </p>
      </div>
    </main>
  );
}
